// 2026-10-19 (user request "angka pengeluaran bisa dibuka detailnya"):
// Dialog rincian item pembentuk nominal pengeluaran di halaman Detail
// Pengeluaran Mingguan. Murni frontend — data item sudah tersedia di baris
// (raw petty cash membawa `items` + realisasi; raw expense membawa entry).
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { formatIDR, fmtDate } from "@/lib/constants";

const STATUS_LABEL = {
  pending: "Menunggu Review", approved: "Disetujui", rejected: "Ditolak",
  transferred: "Sudah Ditransfer", reported: "Sudah Dilaporkan",
};

export default function PengeluaranItemDetailDialog({ open, onOpenChange, row }) {
  if (!row) return null;
  const isPetty = (row.recordKind || row.source) === "petty";
  const raw = row.raw || {};

  if (isPetty) {
    const items = raw.items || [];
    const actualItems = raw.actual_items || [];
    const actualByKey = {};
    for (const a of actualItems) {
      const k = a.item_id || (a.item_index != null ? `idx-${a.item_index}` : null);
      if (k) actualByKey[k] = a;
    }
    const estimated = items.reduce((s, it) => s + (Number(it.unit_price) || 0) * (Number(it.qty) || 1), 0);
    const actual = Number(raw.actual_amount) || 0;
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="expense-item-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight" data-testid="expense-detail-title">
              Detail Item Petty Cash · {raw.week_label || "-"}
            </DialogTitle>
            <DialogDescription className="text-xs">
              Status: <b>{STATUS_LABEL[raw.status] || raw.status || "-"}</b>
              {" · "}Oleh: <b>{raw.owner_name || raw.user_name || raw.created_by_name || "-"}</b>
              {raw.note ? <> · Catatan: {raw.note}</> : null}
            </DialogDescription>
          </DialogHeader>
          {items.length === 0 ? (
            <div className="text-xs text-slate-400 italic py-6 text-center" data-testid="expense-detail-empty">Tidak ada item tercatat.</div>
          ) : (
            <div className="border border-slate-200 overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 border-b border-slate-200 text-left">
                  <tr>
                    <th className="px-3 py-2 overline text-slate-600">Item</th>
                    <th className="px-3 py-2 overline text-slate-600 text-right">Qty</th>
                    <th className="px-3 py-2 overline text-slate-600 text-right">Harga Satuan</th>
                    <th className="px-3 py-2 overline text-slate-600 text-right">Subtotal</th>
                    <th className="px-3 py-2 overline text-slate-600 text-right">Aktual</th>
                    <th className="px-3 py-2 overline text-slate-600">Realisasi</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, i) => {
                    const key = it.id || `idx-${i}`;
                    const act = actualByKey[key];
                    const subtotal = (Number(it.unit_price) || 0) * (Number(it.qty) || 1);
                    const cancelled = !!act?.is_cancelled;
                    return (
                      <tr key={key} className={`border-b border-slate-100 ${cancelled ? "opacity-50" : ""}`} data-testid={`expense-detail-item-${i}`}>
                        <td className="px-3 py-2">
                          <div className="font-semibold text-slate-800">{it.name || "-"}</div>
                          {it.note && <div className="text-[10px] text-slate-500">{it.note}</div>}
                          {cancelled && <span className="inline-block mt-0.5 text-[9px] uppercase font-semibold px-1.5 py-0.5 bg-slate-100 border border-slate-300 text-slate-500">Dibatalkan</span>}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">{Number(it.qty) || 1}{it.unit ? ` ${it.unit}` : ""}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{formatIDR(it.unit_price)}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-semibold">{formatIDR(subtotal)}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-semibold text-emerald-700">
                          {act ? formatIDR(act.actual_amount || 0) : <span className="text-slate-300 font-normal">—</span>}
                        </td>
                        <td className="px-3 py-2 text-[10px] text-slate-500">
                          {act ? (
                            <>
                              {act.actual_transaction_date ? fmtDate(act.actual_transaction_date) : ""}
                              {act.supplier ? ` · ${act.supplier}` : ""}
                              {act.voucher_no ? ` · Vo. ${act.voucher_no}` : ""}
                              {act.note ? ` · ${act.note}` : ""}
                            </>
                          ) : <span className="italic text-slate-300">belum direalisasi</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-slate-50 border-t-2 border-slate-300">
                  <tr>
                    <td colSpan={3} className="px-3 py-2 text-right text-[10px] uppercase tracking-wider font-semibold text-slate-500">Total</td>
                    <td className="px-3 py-2 text-right tabular-nums font-bold text-slate-800" data-testid="expense-detail-total-estimated">{formatIDR(estimated)}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-bold text-emerald-700" data-testid="expense-detail-total-actual">{actual > 0 ? formatIDR(actual) : "—"}</td>
                    <td className="px-3 py-2"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    );
  }

  // Expense entry (project / carry) — detail field lengkap.
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="expense-item-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight" data-testid="expense-detail-title">Detail Pengeluaran</DialogTitle>
          <DialogDescription className="text-xs">
            {row.project ? (row.project.project_name || row.project.wo_number) : "Operasional Kantor"}
            {row.subLabel ? ` · ${row.subLabel}` : ""}
          </DialogDescription>
        </DialogHeader>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
          <div><dt className="overline text-slate-500">Tanggal</dt><dd className="font-semibold text-slate-800">{row.date ? fmtDate(row.date) : "-"}</dd></div>
          <div><dt className="overline text-slate-500">Jumlah</dt><dd className="font-bold text-rose-700 tabular-nums">{formatIDR(row.amount)}</dd></div>
          <div><dt className="overline text-slate-500">Sumber</dt><dd className="text-slate-700">{row.source === "petty" ? "Petty Cash (carry)" : "Expense Project"}</dd></div>
          <div><dt className="overline text-slate-500">Dicatat oleh</dt><dd className="text-slate-700">{row.by || "-"}</dd></div>
          {raw.wo_number && <div><dt className="overline text-slate-500">WO / VO</dt><dd className="text-slate-700">{raw.wo_number}</dd></div>}
          {raw.created_at && <div><dt className="overline text-slate-500">Dibuat</dt><dd className="text-slate-700">{fmtDate(raw.created_at)}</dd></div>}
          <div className="col-span-2"><dt className="overline text-slate-500">Catatan</dt><dd className="text-slate-700 whitespace-pre-wrap">{row.note || "-"}</dd></div>
        </dl>
      </DialogContent>
    </Dialog>
  );
}
